import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { FiSearch, FiX } from "react-icons/fi";
import { Chip, ChipScroll, Empty, Group, SearchField, tokens } from "../activity-logs/styles";
import { SegmentedControl } from "../activity-logs/components/primitives";
import { DirectoryCard, SupplierGrid, Tag } from "../creditos/styles";
import { SupplierAvatar } from "../creditos/components/parts";
import { tokens as T } from "../../styles/designTokens";
import { MISS } from "../revisor/home/AnaliseDashboard";
import { displayName } from "../revisor/home/analiseData";
import { formatCount, formatPercent } from "../revisor/home/revisorData";

/**
 * Fornecedores de uma análise do XML Scan, em cards, com busca, filtros e rolagem
 * infinita. Usado na aba Fornecedores da tela inicial e na tela da análise.
 * `extra` é um filtro vindo de um gráfico: { label, test }.
 */

export const PARTIAL = "#f2b24d";
const STEP = 36;

export const HomeBar = styled.span`
  display: flex;
  gap: 2px;
  height: 3px;
  margin-top: 12px;
  border-radius: 2px;
  overflow: hidden;
  background: rgba(118,118,128,0.1);
  span { display: block; height: 100%; }
`;

export const STATUS_FILTERS = {
  naoConformes: { label: "Não conformes", color: MISS, test: (s) => s.naoConformes > 0 },
  parciais: { label: "Parciais", color: PARTIAL, test: (s) => s.parciais > 0 },
  conformes: { label: "Tudo conforme", color: T.brand, test: (s) => s.total > 0 && s.conformes === s.total },
};

const SupplierCard = React.memo(function SupplierCard({ s, delay, onOpen }) {
  return (
    <DirectoryCard type="button" $delay={delay} onClick={() => onOpen(s.raw)}>
      <span className="head">
        <SupplierAvatar name={s.name} size={40} />
        <span className="who">
          <span className="name" title={s.name}>{displayName(s.name)}</span>
          <span className="id">{s.cnpj || "Sem CNPJ"}</span>
        </span>
      </span>
      <span className="body">
        <span className="amount">
          {formatPercent(s.pct)}
          <span style={{ fontSize: "0.5em", fontWeight: 500, opacity: 0.5, marginLeft: 6, letterSpacing: 0 }}>conformes</span>
        </span>
        <span className="meta">{formatCount(s.conformes)} de {formatCount(s.total)} XMLs</span>
        <HomeBar>
          <span style={{ flexGrow: s.conformes, background: T.brand }} />
          <span style={{ flexGrow: s.parciais, background: PARTIAL }} />
          <span style={{ flexGrow: s.naoConformes, background: MISS }} />
        </HomeBar>
        <span className="taxes">
          {s.naoConformes > 0 && <Tag $color={MISS}>{formatCount(s.naoConformes)} não conformes</Tag>}
          {s.parciais > 0 && <Tag $color={PARTIAL}>{formatCount(s.parciais)} parciais</Tag>}
          {s.naoConformes === 0 && s.parciais === 0 && <Tag $color={T.brand}>Tudo conforme</Tag>}
        </span>
      </span>
    </DirectoryCard>
  );
});

export default function XmlSupplierDirectory({ suppliers, filter, onFilter, extra, onClearExtra, initialSort = "missing", onOpen }) {
  const theme = useTheme();
  const [q, setQ] = useState("");
  const [sort, setSort] = useState(initialSort);
  const [visible, setVisible] = useState(STEP);
  const endRef = useRef(null);

  useEffect(() => { setVisible(STEP); }, [filter, extra]);

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    const digits = term.replace(/\D/g, "");
    let out = suppliers.filter((s) => (!filter || STATUS_FILTERS[filter].test(s))
      && (!extra || extra.test(s))
      && (!term || s.name.toLowerCase().includes(term) || (digits && s.cnpj.includes(digits))));
    if (sort === "missing") out = [...out].sort((x, y) => y.naoConformes - x.naoConformes || y.parciais - x.parciais);
    if (sort === "parciais") out = [...out].sort((x, y) => y.parciais - x.parciais || y.naoConformes - x.naoConformes);
    if (sort === "pct") out = [...out].sort((x, y) => x.pct - y.pct || y.total - x.total);
    if (sort === "name") out = [...out].sort((x, y) => x.name.localeCompare(y.name));
    return out;
  }, [suppliers, q, filter, extra, sort]);

  const hasMore = visible < list.length;
  useEffect(() => {
    if (!hasMore || !endRef.current) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible((v) => v + STEP); }, { rootMargin: "800px" });
    io.observe(endRef.current);
    return () => io.disconnect();
  }, [hasMore, list.length, visible]);

  return (
    <>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <SearchField $max="320px">
          <FiSearch />
          <input placeholder="Buscar por nome ou CNPJ" aria-label="Buscar fornecedor" value={q} onChange={(e) => { setQ(e.target.value); setVisible(STEP); }} />
          {q && <button className="clear" aria-label="Limpar busca" onClick={() => setQ("")}><FiX /></button>}
        </SearchField>
        <div style={{ flex: 1 }} />
        <SegmentedControl
          small
          value={sort}
          onChange={(v) => { setSort(v); setVisible(STEP); }}
          options={[{ key: "missing", label: "Não conformes" }, { key: "parciais", label: "Parciais" }, { key: "pct", label: "Menos conformes" }, { key: "name", label: "Nome" }]}
        />
      </div>
      <ChipScroll style={{ marginTop: 10 }}>
        <Chip $active={!filter} onClick={() => onFilter(null)}>Todos <span style={{ opacity: 0.6, marginLeft: 4 }}>{formatCount(suppliers.length)}</span></Chip>
        {Object.entries(STATUS_FILTERS).map(([key, x]) => (
          <Chip key={key} $active={filter === key} $color={x.color} onClick={() => onFilter(key)}>
            {x.label} <span style={{ opacity: 0.6, marginLeft: 4 }}>{formatCount(suppliers.filter(x.test).length)}</span>
          </Chip>
        ))}
        {extra && <Chip $active onClick={onClearExtra} aria-label={`Remover filtro ${extra.label}`}>{extra.label} <FiX /></Chip>}
      </ChipScroll>
      {list.length === 0 ? (
        <Group style={{ marginTop: 14 }}>
          <Empty>
            <div className="e-icon"><FiSearch /></div>
            <div className="e-title">Nenhum fornecedor</div>
            <div className="e-sub">Nada encontrado com estes filtros.</div>
          </Empty>
        </Group>
      ) : (
        <SupplierGrid style={{ marginTop: 14 }}>
          {list.slice(0, visible).map((s, i) => <SupplierCard key={s.key} s={s} delay={Math.min(i % STEP, 11) * 25} onOpen={onOpen} />)}
        </SupplierGrid>
      )}
      {hasMore && (
        <div ref={endRef} style={{ padding: "18px 0", textAlign: "center", fontSize: "0.78em", color: tokens.label2(theme) }}>
          {formatCount(visible)} de {formatCount(list.length)}
        </div>
      )}
    </>
  );
}
